import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { submitInquiry } from '../services/api';
import toast from 'react-hot-toast';
import './LegalPages.css';

/* ─────────────────────────────────────────────────────────
   Shared layout
───────────────────────────────────────────────────────── */
function LegalLayout({ title, subtitle, icon, lastUpdated, children }) {
  const bodyRef = useScrollReveal();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="legal-page page-enter">
      <div className="legal-hero">
        <div className="legal-hero-inner">
          <div className="legal-icon" data-reveal="scale">{icon}</div>
          <h1 data-reveal="up" data-delay="100">{title}</h1>
          <p data-reveal="up" data-delay="150">{subtitle}</p>
          {lastUpdated && (
            <div className="legal-meta" data-reveal="fade" data-delay="200">
              آخر تحديث: {lastUpdated}
            </div>
          )}
        </div>
      </div>

      <div className="legal-body" ref={bodyRef}>
        <div className="legal-nav">
          <Link to="/">← الرجوع للرئيسية</Link>
          <div className="legal-nav-links">
            <Link to="/privacy">الخصوصية</Link>
            <Link to="/terms">الشروط</Link>
            <Link to="/refund-policy">سياسة الاسترداد</Link>
            <Link to="/about">عن مدرسك</Link>
            <Link to="/contact">اتصل بنا</Link>
          </div>
        </div>

        <div className="legal-content">{children}</div>

        <div className="legal-footer-cta">
          <p>لديك أسئلة؟ تواصل معنا</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:madrasak.eg@gmail.com" className="btn btn-outline btn-sm">📧 madrasak.eg@gmail.com</a>
            <a href="tel:+201013201998" className="btn btn-outline btn-sm">📞 ‎+20 101 320 1998</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, delay = '0' }) {
  return (
    <div className="legal-section" data-reveal="up" data-delay={delay}>
      <h2 className="legal-section-title">{title}</h2>
      <div className="legal-section-body">{children}</div>
    </div>
  );
}

function Item({ title, children }) {
  return (
    <div className="legal-item">
      {title && <h3>{title}</h3>}
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   سياسة الخصوصية
───────────────────────────────────────────────────────── */
export function PrivacyPage() {
  return (
    <LegalLayout icon="🔒" title="سياسة الخصوصية"
      subtitle="نحن نحترم خصوصيتك ونلتزم بحماية بياناتك الشخصية"
      lastUpdated="مارس 2026">
      <div className="legal-intro" data-reveal="up">
        مرحباً بك في منصة <strong>مدرسك</strong>. تصف هذه السياسة كيفية جمعنا لمعلوماتك الشخصية واستخدامها وحمايتها عند استخدامك لمنصتنا التعليمية. باستخدامك للمنصة فإنك توافق على الممارسات الموضحة هنا.
      </div>

      <Section title="١. المعلومات التي نجمعها" delay="50">
        <Item title="أ) المعلومات التي تقدمها مباشرة">
          <ul>
            <li>الاسم الكامل وعنوان البريد الإلكتروني عند التسجيل</li>
            <li>الصورة الشخصية (اختياري)</li>
            <li>معلومات المدرسين: التخصصات، السيرة الذاتية، السعر بالساعة</li>
            <li>بيانات الدفع — تُعالج عبر Paymob ولا نحتفظ ببيانات البطاقات</li>
            <li>المحتوى الذي ترفعه: الفيديوهات، الملفات، التقييمات</li>
          </ul>
        </Item>
        <Item title="ب) المعلومات المجمّعة تلقائياً">
          <ul>
            <li>بيانات الاستخدام: الصفحات التي تزورها، الكورسات التي تشاهدها</li>
            <li>معلومات الجهاز: نوع المتصفح، نظام التشغيل</li>
            <li>عنوان IP والموقع الجغرافي التقريبي</li>
            <li>ملفات تعريف الارتباط (Cookies) لتحسين تجربتك</li>
          </ul>
        </Item>
      </Section>

      <Section title="٢. كيف نستخدم معلوماتك" delay="100">
        <ul>
          <li>تقديم خدمات المنصة وتشغيلها بشكل صحيح</li>
          <li>معالجة المدفوعات وإدارة الاشتراكات</li>
          <li>التواصل معك بشأن حسابك والخدمات</li>
          <li>تحليل بيانات الاستخدام لتحسين الخدمة</li>
          <li>منع الاحتيال وحماية أمن المنصة</li>
          <li>الامتثال للمتطلبات القانونية والتنظيمية</li>
        </ul>
      </Section>

      <Section title="٣. مشاركة المعلومات" delay="150">
        <div className="legal-note">⚠️ <strong>لا نبيع معلوماتك الشخصية لأطراف ثالثة أبداً.</strong></div>
        <ul>
          <li><strong>مزودو الخدمات:</strong> Paymob (معالجة الدفع)، Cloudinary (تخزين الوسائط)، Firebase (قاعدة البيانات)</li>
          <li><strong>المتطلبات القانونية:</strong> إذا طُلب ذلك بموجب القانون أو أمر قضائي</li>
          <li><strong>حماية الحقوق:</strong> لحماية حقوق أو سلامة المنصة والمستخدمين</li>
        </ul>
      </Section>

      <Section title="٤. أمان البيانات" delay="200">
        <ul>
          <li>تشفير SSL/TLS لجميع البيانات المنقولة</li>
          <li>تشفير كلمات المرور وبيانات الدفع الحساسة</li>
          <li>الفيديوهات محمية بـ Cloudinary Signed URLs تنتهي خلال ساعتين</li>
          <li>التحقق من HMAC على كل معاملة Paymob لمنع التلاعب</li>
          <li>مراجعات أمنية دورية للنظام</li>
        </ul>
      </Section>

      <Section title="٥. حقوقك" delay="250">
        <ul>
          <li><strong>الوصول:</strong> طلب نسخة من بياناتك الشخصية</li>
          <li><strong>التصحيح:</strong> تحديث أو تصحيح معلوماتك في أي وقت</li>
          <li><strong>الحذف:</strong> طلب حذف حسابك وبياناتك</li>
          <li><strong>الاعتراض:</strong> الاعتراض على معالجة بياناتك لأغراض التسويق</li>
        </ul>
        <p>لممارسة أي من هذه الحقوق تواصل معنا على <a href="mailto:privacy@madrasak.com">privacy@madrasak.com</a></p>
      </Section>

      <Section title="٦. الاحتفاظ بالبيانات" delay="300">
        <ul>
          <li>تُحذف بياناتك الشخصية خلال 30 يوماً من حذف الحساب</li>
          <li>تُحتفظ بسجلات المعاملات المالية 7 سنوات وفقاً للمتطلبات القانونية</li>
        </ul>
      </Section>

      <Section title="٧. تواصل معنا" delay="350">
        <div className="legal-contact">
          <span>📧 <a href="mailto:privacy@madrasak.com">privacy@madrasak.com</a></span>
          <span>📞 <a href="tel:+201013201998">‎+20 101 320 1998</a></span>
        </div>
      </Section>
    </LegalLayout>
  );
}

/* ─────────────────────────────────────────────────────────
   الشروط والأحكام
───────────────────────────────────────────────────────── */
export function TermsPage() {
  return (
    <LegalLayout icon="📜" title="الشروط والأحكام"
      subtitle="اقرأ هذه الشروط بعناية قبل استخدام منصة مدرسك"
      lastUpdated="مارس 2026">
      <div className="legal-intro" data-reveal="up">
        تحكم هذه الشروط استخدامك لمنصة <strong>مدرسك</strong>. باستخدامك للمنصة فإنك تقر بقراءة هذه الشروط والموافقة عليها.
      </div>

      <Section title="١. تعريفات" delay="50">
        <ul>
          <li><strong>المنصة:</strong> موقع وتطبيق مدرسك للتعليم الإلكتروني</li>
          <li><strong>الطالب:</strong> المستخدم المسجّل الذي يشترك في الكورسات</li>
          <li><strong>المدرّس:</strong> المستخدم المعتمد الذي ينشئ ويبيع الكورسات</li>
          <li><strong>المحتوى:</strong> أي مواد تعليمية، فيديوهات، نصوص تُنشر على المنصة</li>
        </ul>
      </Section>

      <Section title="٢. شروط التسجيل" delay="100">
        <ul>
          <li>يجب أن يكون عمرك 13 سنة أو أكثر للتسجيل</li>
          <li>يجب تقديم معلومات دقيقة وصحيحة عند التسجيل</li>
          <li>أنت مسؤول عن الحفاظ على سرية كلمة مرورك</li>
          <li>يُحظر إنشاء أكثر من حساب واحد لكل شخص</li>
        </ul>
      </Section>

      <Section title="٣. الدفع والاسترداد" delay="150">
        <ul>
          <li>جميع الأسعار بالجنيه المصري (ج.م)</li>
          <li>تُعالج المدفوعات بأمان عبر بوابة <strong>Paymob</strong> المرخّصة من البنك المركزي المصري</li>
          <li>للاطلاع على سياسة الاسترداد الكاملة، <Link to="/refund-policy">اضغط هنا</Link></li>
        </ul>
      </Section>

      <Section title="٤. سياسة المحتوى" delay="200">
        <Item title="للمدرسين:">
          <ul>
            <li>المحتوى الذي تنشره يجب أن يكون أصلياً وملكاً لك</li>
            <li>يُحظر نشر محتوى مسروق أو ينتهك حقوق الملكية الفكرية</li>
            <li>كل الكورسات تخضع للمراجعة والموافقة قبل النشر</li>
          </ul>
        </Item>
        <Item title="للطلاب:">
          <ul>
            <li>المحتوى للاستخدام الشخصي فقط</li>
            <li>يُحظر تسجيل أو إعادة توزيع الفيديوهات</li>
            <li>يُحظر مشاركة بيانات الاشتراك مع الآخرين</li>
          </ul>
        </Item>
      </Section>

      <Section title="٥. الملكية الفكرية" delay="250">
        <ul>
          <li>جميع المحتويات التعليمية ملك لأصحابها من المدرسين</li>
          <li>العلامة التجارية "مدرسك" وتصميم المنصة ملك حصري للشركة</li>
          <li>يُحظر نسخ أو توزيع أو استخدام المحتوى تجارياً دون إذن</li>
        </ul>
      </Section>

      <Section title="٦. إخلاء المسؤولية" delay="300">
        <ul>
          <li>جودة المحتوى التعليمي مسؤولية المدرّس بالدرجة الأولى</li>
          <li>لا نضمن نتائج تعليمية محددة من استخدام المنصة</li>
          <li>لسنا مسؤولين عن أي خسائر ناتجة عن انقطاع الخدمة</li>
        </ul>
      </Section>

      <Section title="٧. القانون المطبّق" delay="350">
        <p>تخضع هذه الشروط لقوانين جمهورية مصر العربية. أي نزاعات تُحسم أمام محاكم القاهرة المختصة.</p>
      </Section>
    </LegalLayout>
  );
}

/* ─────────────────────────────────────────────────────────
   سياسة الاسترداد
───────────────────────────────────────────────────────── */
export function RefundPolicyPage() {
  return (
    <LegalLayout icon="💸" title="سياسة الاسترداد"
      subtitle="نريدك راضياً — هذه شروط استرداد الأموال الواضحة"
      lastUpdated="مارس 2026">
      <div className="legal-intro" data-reveal="up">
        في منصة <strong>مدرسك</strong> نسعى دائماً لرضاك التام. إذا لم تكن راضياً عن كورس اشتركت فيه، يمكنك طلب استرداد أموالك وفقاً للشروط التالية.
      </div>

      <div className="legal-highlight" data-reveal="scale">
        <div className="legal-highlight-grid">
          <div className="lh-item">
            <span className="lh-num">7 أيام</span>
            <span className="lh-lbl">مدة ضمان الاسترداد</span>
          </div>
          <div className="lh-div" />
          <div className="lh-item">
            <span className="lh-num">20%</span>
            <span className="lh-lbl">الحد الأقصى للمشاهدة</span>
          </div>
          <div className="lh-div" />
          <div className="lh-item">
            <span className="lh-num">5–7 أيام</span>
            <span className="lh-lbl">مدة إعادة المبلغ</span>
          </div>
        </div>
      </div>

      <Section title="١. حالات الاسترداد المقبولة" delay="50">
        <ul>
          <li>✅ طلب الاسترداد خلال <strong>7 أيام</strong> من تاريخ الشراء</li>
          <li>✅ لم تُشاهد أكثر من <strong>20%</strong> من محتوى الكورس</li>
          <li>✅ وجود خلل تقني موثّق يمنع مشاهدة الكورس</li>
          <li>✅ محتوى الكورس لا يتطابق مع وصفه المُعلن</li>
        </ul>
      </Section>

      <Section title="٢. حالات رفض الاسترداد" delay="100">
        <ul>
          <li>❌ مرور أكثر من 7 أيام على الشراء</li>
          <li>❌ مشاهدة أكثر من 20% من محتوى الكورس</li>
          <li>❌ الكورسات المجانية لا تخضع لسياسة الاسترداد</li>
          <li>❌ وجود دليل على تنزيل المحتوى أو مشاركته</li>
          <li>❌ طلبات الاسترداد المتكررة من نفس الحساب (أكثر من مرتين خلال 6 أشهر)</li>
        </ul>
      </Section>

      <Section title="٣. كيفية طلب الاسترداد" delay="150">
        <ol style={{ paddingRight: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <li>تواصل معنا عبر البريد الإلكتروني: <a href="mailto:support@madrasak.com">support@madrasak.com</a></li>
          <li>أرسل: اسمك الكامل، البريد الإلكتروني، اسم الكورس، سبب الاسترداد</li>
          <li>سيرد فريقنا خلال <strong>24-48 ساعة</strong> عمل</li>
          <li>في حالة الموافقة، يُعاد المبلغ خلال <strong>5-7 أيام عمل</strong> عبر نفس وسيلة الدفع الأصلية</li>
        </ol>
      </Section>

      <Section title="٤. طريقة إعادة المبلغ" delay="200">
        <ul>
          <li><strong>بطاقة ائتمان/خصم:</strong> يُعاد للبطاقة خلال 5-7 أيام عمل</li>
          <li><strong>فودافون كاش / اورنج كاش:</strong> يُعاد للمحفظة خلال 3-5 أيام عمل</li>
          <li>قد تستغرق البنوك وقتاً إضافياً لظهور المبلغ في حسابك</li>
        </ul>
      </Section>

      <Section title="٥. الاستثناءات الخاصة" delay="250">
        <ul>
          <li>الكورسات التي تضمنت حصصاً لايف تم حضورها لا تُسترد</li>
          <li>الكوبونات المستخدمة في الشراء لا تُعاد كنقود نقدية</li>
          <li>في حالة الخلل التقني الموثّق، يُقبل الاسترداد في أي وقت بصرف النظر عن نسبة المشاهدة</li>
        </ul>
      </Section>

      <Section title="٦. تواصل معنا" delay="300">
        <div className="legal-contact">
          <span>📧 <a href="mailto:support@madrasak.com">support@madrasak.com</a></span>
          <span>📞 <a href="tel:+201013201998">‎+20 101 320 1998</a></span>
          <span>⏱ أوقات العمل: السبت – الخميس، 9ص – 9م</span>
        </div>
      </Section>
    </LegalLayout>
  );
}

/* ─────────────────────────────────────────────────────────
   سياسة الشحن (Shipping Policy — for digital products)
───────────────────────────────────────────────────────── */
export function ShippingPolicyPage() {
  return (
    <LegalLayout icon="📦" title="سياسة التسليم"
      subtitle="مدرسك منصة رقمية — المحتوى يُسلَّم فورياً عبر الإنترنت"
      lastUpdated="مارس 2026">
      <div className="legal-intro" data-reveal="up">
        منصة <strong>مدرسك</strong> تقدم منتجات وخدمات رقمية بالكامل. لا يوجد شحن فعلي — كل المحتوى يُسلَّم إلكترونياً فور تأكيد الدفع.
      </div>

      <Section title="١. طريقة التسليم" delay="50">
        <ul>
          <li>🎓 <strong>الكورسات المسجلة:</strong> متاحة فوراً بعد تأكيد الدفع عبر لوحة التحكم</li>
          <li>🔴 <strong>الحصص اللايف:</strong> يُرسل رابط الانضمام عبر البريد الإلكتروني قبل الحصة بـ 24 ساعة</li>
          <li>📄 <strong>الملفات والشهادات:</strong> متاحة للتحميل من داخل المنصة مباشرة</li>
        </ul>
      </Section>

      <Section title="٢. وقت التسليم" delay="100">
        <ul>
          <li>الكورسات المسجلة: <strong>فوري</strong> بعد تأكيد الدفع (في الغالب خلال ثوانٍ)</li>
          <li>في حالة تأخر الوصول أكثر من 10 دقائق، تواصل معنا فوراً</li>
          <li>أوقات الذروة قد تستغرق حتى 30 دقيقة في حالات نادرة</li>
        </ul>
      </Section>

      <Section title="٣. مشاكل الوصول" delay="150">
        <p>إذا واجهت صعوبة في الوصول للمحتوى بعد الدفع:</p>
        <ol style={{ paddingRight: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <li>تحقق من البريد الإلكتروني (بما فيه مجلد Spam)</li>
          <li>سجّل خروجاً ودخولاً من جديد للمنصة</li>
          <li>أعد تحميل الصفحة وانتظر 5 دقائق</li>
          <li>إذا استمرت المشكلة، تواصل مع الدعم الفني فوراً</li>
        </ol>
      </Section>

      <Section title="٤. الوصول عبر الأجهزة" delay="200">
        <ul>
          <li>يمكن الوصول للمحتوى من أي جهاز: كمبيوتر، تابلت، أو هاتف</li>
          <li>يُنصح باستخدام متصفح Chrome أو Firefox لأفضل تجربة</li>
          <li>الفيديوهات تتطلب اتصالاً بالإنترنت — لا يوجد تحميل دون اتصال حالياً</li>
        </ul>
      </Section>

      <Section title="٥. تواصل معنا" delay="250">
        <div className="legal-contact">
          <span>📧 <a href="mailto:support@madrasak.com">support@madrasak.com</a></span>
          <span>📞 <a href="tel:+201013201998">‎+20 101 320 1998</a></span>
        </div>
      </Section>
    </LegalLayout>
  );
}

/* ─────────────────────────────────────────────────────────
   اتصل بنا — Contact Us
───────────────────────────────────────────────────────── */
export function ContactPage() {
  const [form, setForm]     = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent]     = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await submitInquiry(form);
      setSent(true);
      toast.success('تم إرسال رسالتك بنجاح! سنقوم بالرد عليك في أقرب وقت.');
    } catch (err) {
      toast.error('فشل إرسال الرسالة، يرجى المحاولة مرة أخرى.');
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout icon="📩" title="اتصل بنا"
      subtitle="فريق مدرسك دائماً هنا لمساعدتك — ابعتلنا أي وقت">
      <div className="contact-grid" data-reveal="up">
        {/* Contact info */}
        <div className="contact-info">
          <div className="contact-card-sidebar">
            <h3>معلومات التواصل</h3>

            <div className="contact-item">
              <div className="ci-icon">📧</div>
              <div>
                <div className="ci-label">البريد الإلكتروني</div>
                <a href="mailto:madrasak.eg@gmail.com" className="ci-value">madrasak.eg@gmail.com</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="ci-icon">📞</div>
              <div>
                <div className="ci-label">رقم الهاتف</div>
                <a href="tel:+201013201998" className="ci-value" dir="ltr">+20 101 320 1998</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="ci-icon">🟢</div>
              <div>
                <div className="ci-label">واتساب</div>
                <a href="https://wa.me/201013201998" className="ci-value" dir="ltr" target="_blank" rel="noreferrer">+20 101 320 1998</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="ci-icon">📍</div>
              <div>
                <div className="ci-label">العنوان</div>
                <div className="ci-value">القاهرة، جمهورية مصر العربية</div>
              </div>
            </div>

            <div className="contact-item">
              <div className="ci-icon">⏱</div>
              <div>
                <div className="ci-label">أوقات الدعم</div>
                <div className="ci-value">السبت – الخميس: 9 ص – 9 م</div>
              </div>
            </div>

            {/* Quick links */}
            <div className="contact-links" style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h4 style={{ marginBottom: 4 }}>روابط سريعة</h4>
              <Link to="/#faq" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textDecoration: 'none', color: 'var(--text)' }}>
                <span>❓</span> الأسئلة الشائعة
              </Link>
              <Link to="/refund-policy" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textDecoration: 'none', color: 'var(--text)' }}>
                <span>💸</span> سياسة الاسترداد
              </Link>
              <Link to="/privacy" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textDecoration: 'none', color: 'var(--text)' }}>
                <span>🔒</span> سياسة الخصوصية
              </Link>
              <Link to="/about" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textDecoration: 'none', color: 'var(--text)' }}>
                <span>🏢</span> عن مدرسك
              </Link>
            </div>
          </div>
        </div>

        {/* Contact form */}
        <div className="contact-form-wrap">
          <h3>أرسل رسالة</h3>
          {sent ? (
            <div className="contact-success" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
              <h4 style={{ marginBottom: 8 }}>شكراً لتواصلك معنا!</h4>
              <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>لقد استلمنا رسالتك وسيقوم فريق الدعم بمراجعتها والرد عليك في أقرب وقت ممكن عبر البريد الإلكتروني.</p>
              <button className="btn btn-outline btn-sm" onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}>إرسال رسالة أخرى</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label className="form-label">الاسم الكامل *</label>
                <input className="form-input" placeholder="اسمك" value={form.name} required
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">البريد الإلكتروني *</label>
                <input className="form-input" type="email" placeholder="email@example.com" dir="ltr"
                  value={form.email} required onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">رقم الهاتف</label>
                <input className="form-input" placeholder="010XXXXXXXX"
                  value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">الموضوع</label>
                <select className="form-input" value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}>
                  <option value="">اختر موضوع الرسالة</option>
                  <option>استفسار عن كورس</option>
                  <option>مشكلة تقنية</option>
                  <option>طلب استرداد</option>
                  <option>الانضمام كمدرّس</option>
                  <option>شراكات ومقترحات</option>
                  <option>أخرى</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الرسالة *</label>
                <textarea className="form-input" rows={5} placeholder="اكتب رسالتك هنا..." required
                  value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={sending}>
                {sending ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : '📩 إرسال الرسالة'}
              </button>
            </form>
          )}
        </div>
      </div>
    </LegalLayout>
  );
}

import { getPublicStats } from '../services/api';

/* ─────────────────────────────────────────────────────────
   عن مدرسك — About Us
───────────────────────────────────────────────────────── */
export function AboutPage() {
  const [stats, setStats] = useState({ teachers: 0, courses: 0, enrollments: 0, students: 0 });

  useEffect(() => {
    getPublicStats().then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <LegalLayout icon="🎓" title="عن مدرسك"
      subtitle="منصة تعليمية مصرية تربط المدرسين المستقلين بالطلاب">
      <div className="legal-intro" data-reveal="up">
        <strong>مدرسك</strong> هي منصة تعليمية إلكترونية مصرية رائدة، تهدف إلى إحداث ثورة في كيفية وصول الطلاب للمحتوى التعليمي عالي الجودة. نجمع بين أحدث التقنيات وأفضل الكفاءات التدريسية لنوفر تجربة تعليمية فريدة تتجاوز حدود الفصل الدراسي التقليدي.
      </div>

      {/* Stats */}
      <div className="legal-highlight" data-reveal="scale">
        <div className="legal-highlight-grid">
          <div className="lh-item">
            <span className="lh-num">+{stats.students || 0}</span>
            <span className="lh-lbl">طالب مسجّل</span>
          </div>
          <div className="lh-div" />
          <div className="lh-item">
            <span className="lh-num">+{stats.teachers || 0}</span>
            <span className="lh-lbl">مدرّس معتمد</span>
          </div>
          <div className="lh-div" />
          <div className="lh-item">
            <span className="lh-num">+{stats.courses || 0}</span>
            <span className="lh-lbl">كورس منشور</span>
          </div>
        </div>
      </div>

      <Section title="قصتنا وتاريخنا" delay="50">
        <p>بدأت رحلة <strong>مدرسك</strong> في عام 2024 من قلب القاهرة، بمدفوع برؤية طموحة لمجموعة من المطورين والتربويين المصريين. لاحظنا وجود فجوة متزايدة بين جودة التعليم المتاحة وتكلفتها المرتفعة، مما يشكل عائقاً أمام الكثير من الطلاب المجتهدين.</p>
        <p>قررنا بناء جسر رقمي يجمع بين المدرسين المبدعين الذين يمتلكون الشغف والقدرة على تبسيط المعلومة، وبين الطلاب في كافة أنحاء الجمهورية الذين يبحثون عن بديل عصري ومرن للدروس الخصوصية التقليدية.</p>
        <p>منذ انطلاقنا، نجحنا في بناء مجتمع تعليمي نشط يضم آلاف الطلاب ومئات المدرسين المتميزين في مختلف المواد والمراحل الدراسية، من المرحلة الابتدائية وحتى التعليم الجامعي والمهني.</p>
      </Section>

      <Section title="رؤيتنا" delay="75">
        <p>نسعى لأن نكون المنصة التعليمية الأولى في الشرق الأوسط التي تمكن كل طالب من الوصول لأفضل المدرسين بضغطة زر، وتوفر لكل مدرس محترف الأدوات التقنية اللازمة لبناء مدرسته الافتراضية الخاصة، مع الحفاظ على التوازن بين الجودة العالية والتكلفة العادلة.</p>
      </Section>

      <Section title="أهدافنا الاستراتيجية" delay="100">
        <ul>
          <li><strong>ديمقراطية التعليم:</strong> ضمان وصول المحتوى التعليمي المتميز لكل طالب مصري، سواء في العاصمة أو في أبعد قرى الصعيد والدلتا.</li>
          <li><strong>تمكين المدرس المستقل:</strong> توفير منصة متكاملة للمدرسين لإدارة محتواهم، تواصلهم مع الطلاب، وتحقيق دخل عادل يكافئ مجهودهم وإبداعهم.</li>
          <li><strong>الابتكار التعليمي:</strong> دمج التقنيات الحديثة مثل التعلم المصغر (Micro-learning) عبر الريلز، والاختبارات التفاعلية، والشهادات الرقمية لجعل عملية التعلم أكثر فاعلية وإمتاعاً.</li>
          <li><strong>بناء جيل المبدعين:</strong> التركيز ليس فقط على التحصيل الأكاديمي، بل على تطوير مهارات التفكير النقدي والتعلم الذاتي لدى الطلاب.</li>
        </ul>
      </Section>

      <Section title="ما يميّزنا" delay="150">
        <div className="about-features">
          {[
            { icon: '🔒', title: 'محتوى محمي', desc: 'فيديوهات محمية بتقنية Cloudinary مع signed URLs وحماية من التسجيل' },
            { icon: '💳', title: 'دفع آمن', desc: 'Paymob مرخّصة من البنك المركزي — بطاقات + محافظ إلكترونية مصرية' },
            { icon: '📱', title: 'تصميم متجاوب', desc: 'تجربة مثالية على الهاتف والتابلت والكمبيوتر' },
            { icon: '📊', title: 'تتبع التقدم', desc: 'تتبع تقدمك في كل كورس مع شهادات إتمام قابلة للتحميل' },
            { icon: '🎯', title: 'اختبارات تفاعلية', desc: 'اختبارات بعد كل درس مع تصحيح فوري وشرح للإجابات' },
            { icon: '🤝', title: 'دعم المدرسين', desc: '75% من أرباح الكورس تذهب للمدرّس مباشرة' },
          ].map(f => (
            <div key={f.title} className="about-feature-card">
              <div className="afc-icon">{f.icon}</div>
              <div className="afc-title">{f.title}</div>
              <div className="afc-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="فريقنا" delay="200">
        <p>مدرسك تم تطويرها بواسطة <strong>Mohamed Reda Web Solutions</strong> — فريق من المطورين المصريين المتخصصين في بناء منصات التعليم الإلكتروني.</p>
        <div className="legal-contact" style={{ marginTop: 12 }}>
          <span>📧 <a href="mailto:madrasak.eg@gmail.com">madrasak.eg@gmail.com</a></span>
          <span>📞 <a href="tel:+201013201998">‎+20 101 320 1998</a></span>
          <span>📍 القاهرة، مصر</span>
        </div>
      </Section>

      <Section title="التواصل الاجتماعي" delay="250">
        <div className="about-socials">
          <a href="https://facebook.com" target="_blank" rel="noreferrer" className="social-link social-fb">Facebook</a>
          <a href="https://instagram.com" target="_blank" rel="noreferrer" className="social-link social-ig">Instagram</a>
          <a href="https://tiktok.com"   target="_blank" rel="noreferrer" className="social-link social-tt">TikTok</a>
          <a href="https://youtube.com"  target="_blank" rel="noreferrer" className="social-link social-yt">YouTube</a>
        </div>
      </Section>

      <div className="legal-cta-box" data-reveal="up">
        <h3>انضم إلى مدرسك اليوم</h3>
        <p>سواء كنت طالباً تريد التعلم أو مدرساً تريد نشر محتواك</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/register" className="btn btn-primary btn-lg">سجّل كطالب ←</Link>
          <Link to="/register?role=teacher" className="btn btn-outline btn-lg">انضم كمدرّس ←</Link>
        </div>
      </div>
    </LegalLayout>
  );
}

/* ─────────────────────────────────────────────────────────
   اتفاقية المدرسين
───────────────────────────────────────────────────────── */
export function TeacherAgreementPage() {
  const [commission, setCommission] = useState(15);

  useEffect(() => {
    getPublicStats().then(r => setCommission(r.data.commission || 15)).catch(() => {});
  }, []);

  return (
    <LegalLayout icon="🤝" title="اتفاقية المدرسين"
      subtitle="اتفاقية الشراكة بين مدرسك والمدرسين المستقلين"
      lastUpdated="مارس 2026">
      <div className="legal-intro" data-reveal="up">
        تُحدد هذه الاتفاقية حقوق والتزامات المدرسين المستقلين على منصة <strong>مدرسك</strong>. بإنشاء حساب مدرّس والحصول على موافقة الإدارة فإنك توافق على جميع بنود هذه الاتفاقية.
      </div>

      <div className="legal-highlight" data-reveal="scale">
        <div className="legal-highlight-grid">
          <div className="lh-item"><span className="lh-num">{100 - commission}%</span><span className="lh-lbl">حصتك من كل اشتراك</span></div>
          <div className="lh-div" />
          <div className="lh-item"><span className="lh-num">{commission}%</span><span className="lh-lbl">حصة المنصة للتشغيل</span></div>
          <div className="lh-div" />
          <div className="lh-item"><span className="lh-num">7 أيام</span><span className="lh-lbl">دورة الدفع الأسبوعية</span></div>
        </div>
      </div>

      <Section title="١. شروط القبول" delay="100">
        <ul>
          <li>التحقق من هويتك وتخصصك العلمي</li>
          <li>تقديم نموذج كورس أو درس تجريبي للمراجعة</li>
          <li>الموافقة على معايير جودة المحتوى</li>
        </ul>
      </Section>

      <Section title="٢. نموذج الأرباح" delay="150">
        <ul>
          <li><strong>نسبة المدرّس: {100 - commission}%</strong> من سعر كل اشتراك بعد خصم رسوم بوابة الدفع</li>
          <li><strong>نسبة المنصة: {commission}%</strong> لتغطية التشغيل، معالجة المدفوعات، التسويق</li>
          <li>الحد الأدنى للسحب: 100 ج.م</li>
          <li>يحق للمنصة تعديل نسبة العمولة بإشعار مسبق 30 يوماً</li>
        </ul>
      </Section>

      <Section title="٣. الملكية الفكرية" delay="200">
        <ul>
          <li>تحتفظ بملكية كامل المحتوى الذي تنشئه</li>
          <li>تمنح المنصة حق استضافة وتوزيع المحتوى عبر المنصة فقط</li>
          <li>يمكنك بيع نفس المحتوى على منصات أخرى ما لم تتعاقد على حصرية</li>
        </ul>
      </Section>

      <Section title="٤. الالتزامات التعليمية" delay="250">
        <ul>
          <li>الرد على استفسارات الطلاب خلال 48 ساعة</li>
          <li>تحديث المحتوى عند وجود أخطاء أو معلومات قديمة</li>
          <li>الالتزام بمواعيد الحصص اللايف المُعلن عنها</li>
          <li>فيديوهات بجودة لا تقل عن 720p مع صوت واضح</li>
        </ul>
      </Section>

      <Section title="٥. سياسة الاسترداد" delay="300">
        <ul>
          <li>عند موافقة المنصة على طلب استرداد من طالب، تُخصم المبالغ من أرباحك</li>
          <li>في حالة وجود عيب تقني في كورسك تتحمل المنصة تكلفة الاسترداد</li>
          <li>يحق لك الاعتراض على طلبات الاسترداد خلال 7 أيام</li>
        </ul>
      </Section>

      <div className="legal-cta-box" data-reveal="up">
        <h3>مستعد للانضمام؟</h3>
        <p>سجّل كمدرّس الآن وابدأ رحلتك في التعليم الإلكتروني</p>
        <Link to="/register?role=teacher" className="btn btn-primary btn-lg">انضم كمدرّس مجاناً ←</Link>
      </div>
    </LegalLayout>
  );
}
